import { PAGE_SIZE_MAX, PARTICIPANTS_PAGE_SIZE_DEFAULT, searchParam } from "@seagames/contracts";
import { z } from "zod";

/**
 * URL search-param fields shared by the Participants pages (athletes, teams &
 * entries, NOCs). Every field drops a bad value (`.catch(undefined)`) instead
 * of failing the page: a hand-edited or stale link still opens. Limits come
 * from `@seagames/contracts` only (CLAUDE.md bug 4).
 */

/** A row id (sport, event, NOC…). */
export const idParam = z.string().uuid().optional().catch(undefined);

/** Offset page, 1-based; absent = 1. */
export const pageParam = z.coerce.number().int().min(1).optional().catch(undefined);

/** The rows-per-page picker. Never above the API's ceiling; the API's own default is one of them. */
export const PAGE_SIZE_CHOICES: readonly number[] = [25, PARTICIPANTS_PAGE_SIZE_DEFAULT, PAGE_SIZE_MAX].filter(
	(n, i, all) => n <= PAGE_SIZE_MAX && all.indexOf(n) === i,
);

/** Rows per page; absent = the page's default. Only the picker's choices are accepted. */
export const sizeParam = z.coerce
	.number()
	.int()
	.refine((n) => PAGE_SIZE_CHOICES.includes(n))
	.optional()
	.catch(undefined);

/** Free text, bounded exactly as the API's `search` (trimmed first, then measured). */
export const textParam = searchParam.optional().catch(undefined);

/** The longest search the API accepts — the text box's `maxLength`. */
export const SEARCH_MAX: number = searchParam.maxLength ?? 100;

/** A draft's text, as it goes into the URL: trimmed, cut to the API's limit, empty = absent. */
export function cleanText(value: string): string | undefined {
	const v = value.trim().slice(0, SEARCH_MAX).trim();
	return v === "" ? undefined : v;
}

/** The last page that has rows (at least 1). */
export function lastPage(total: number, pageSize: number): number {
	return Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
}

/**
 * A page past the end — a link from when the list was longer, or rows that
 * went away: the page to show instead, or null when `page` is fine.
 */
export function pageBeyondEnd(page: number, total: number, pageSize: number): number | null {
	const last = lastPage(total, pageSize);
	return total > 0 && page > last ? last : null;
}

/** URL page → the value to store: page 1 is the default, so it is left out of the URL. */
export const pageToUrl = (page: number): number | undefined => (page > 1 ? page : undefined);

/** URL size → the value to store: the default size is left out of the URL. */
export const sizeToUrl = (size: number, fallback: number): number | undefined => (size === fallback ? undefined : size);
